import React from 'react';

// CSS for shake animation
const shakeStyles = `
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
    20%, 40%, 60%, 80% { transform: translateX(2px); }
  }
  .animate-shake {
    animation: shake 0.5s ease-in-out;
  }
`;

/**
 * AlertMessage - A reusable component for displaying error, success, warning, and info messages
 * 
 * @param {string} message - The message to display
 * @param {string} type - Type of alert: 'error', 'success', 'warning', 'info' (default: 'error')
 * @param {boolean} dismissible - Whether the alert can be dismissed (default: true)
 * @param {function} onDismiss - Callback function when alert is dismissed
 * @param {string} className - Additional CSS classes to apply
 * @param {boolean} animate - Whether to apply shake animation (default: true for errors)
 */
const AlertMessage = ({ 
  message, 
  type = 'error', 
  dismissible = true, 
  onDismiss, 
  className = '',
  animate = null
}) => {
  if (!message) return null;

  // Determine if animation should be applied
  const shouldAnimate = animate !== null ? animate : (type === 'error');

  // Get styles based on type
  const getStyles = () => {
    const baseStyles = "rounded-lg p-4 border-2";
    const animationClass = shouldAnimate ? "animate-shake" : "";
    
    switch (type) {
      case 'success':
        return `${baseStyles} bg-green-50 border-green-300 text-green-700 ${animationClass}`;
      case 'warning':
        return `${baseStyles} bg-yellow-50 border-yellow-300 text-yellow-700 ${animationClass}`;
      case 'info':
        return `${baseStyles} bg-blue-50 border-blue-300 text-blue-700 ${animationClass}`;
      case 'error':
      default:
        return `${baseStyles} bg-red-50 border-red-300 text-red-700 ${animationClass}`;
    }
  };

  // Get icon based on type
  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✅';
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      case 'error':
      default:
        return '⚠️';
    }
  };

  return (
    <>
      <style>{shakeStyles}</style>
      <div className={`${getStyles()} ${className}`}>
        <div className="flex items-center gap-2">
          <span className="text-lg flex-shrink-0">{getIcon()}</span>
          <p className={`font-semibold flex-1 ${type === 'error' ? 'text-red-700' : type === 'success' ? 'text-green-700' : type === 'warning' ? 'text-yellow-700' : 'text-blue-700'}`}>
            {message}
          </p>
          {dismissible && onDismiss && (
            <button
              onClick={onDismiss}
              className={`ml-auto flex-shrink-0 text-xl font-bold transition-colors ${
                type === 'error' 
                  ? 'text-red-500 hover:text-red-700' 
                  : type === 'success'
                  ? 'text-green-500 hover:text-green-700'
                  : type === 'warning'
                  ? 'text-yellow-500 hover:text-yellow-700'
                  : 'text-blue-500 hover:text-blue-700'
              }`}
              aria-label="Dismiss alert"
            >
              ×
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default AlertMessage;
